import { config } from '@/config'
import { getAdpApiHeaders } from './adpDataSources.api'

interface S3LinksResponse {
    success: boolean
    status: number
    message: string
    data: {
        files: Array<{
            file_name: string
            signed_token: string
        }>
    }
}

/**
 * Step 1: Get signed token(s) for a pipeline's failed records download
 */
export async function fetchDQSignedTokens(dataFlowId: string): Promise<S3LinksResponse['data']['files']> {
    const base = (config.adpApiUrl as string).replace(/\/+$/, '')
    const url = `${base}/downloads/s3-links`

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAdpApiHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            download_request_type: 'dq_failed_records_download',
            data_flow_id: Number(dataFlowId),
        }),
    })

    const json = (await res.json().catch(() => ({}))) as S3LinksResponse
    if (!res.ok || !json.success) {
        throw new Error(json.message || `Failed to fetch download links (${res.status})`)
    }

    return json.data.files
}

/**
 * Step 2: Download the actual file using the signed token.
 */
export async function downloadS3File(signedToken: string, fileName: string): Promise<void> {
    const base = (config.adpApiUrl as string).replace(/\/+$/, '')
    const url = `${base}/downloads/s3-file?signed_token=${encodeURIComponent(signedToken)}`

    const res = await fetch(url, {
        method: 'GET',
        headers: {
            ...getAdpApiHeaders(),
            accept: '*/*',
        },
    })

    if (!res.ok) {
        throw new Error(`Download failed (${res.status})`)
    }

    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)

    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(objectUrl)
}

/**
 * Full flow: fetch signed tokens then download each file
 */
export async function downloadDQReport(dataFlowId: string): Promise<void> {
    const files = await fetchDQSignedTokens(dataFlowId)

    if (!files || files.length === 0) {
        throw new Error('No files available for download')
    }

    await Promise.all(
        files.map((file) => downloadS3File(file.signed_token, file.file_name))
    )
}

export async function fetchDestinationFileSignedTokens(
    folderName: string,
    targetTableId: number,
): Promise<S3LinksResponse['data']['files']> {
    const base = (config.adpApiUrl as string).replace(/\/+$/, '')
    const url = `${base}/downloads/s3-links`

    const res = await fetch(url, {
        method: 'POST',
        headers: {
            ...getAdpApiHeaders(),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            download_request_type: 'destination_file_download',
            folder_name: folderName,
            target_table_id: targetTableId,
        }),
    })

    const json = (await res.json().catch(() => ({}))) as S3LinksResponse
    if (!res.ok || !json.success) {
        throw new Error(json.message || `Failed to fetch download links (${res.status})`)
    }

    return json.data.files
}

export async function downloadDestinationFile(
    folderName: string,
    targetTableId: number,
): Promise<void> {
    const files = await fetchDestinationFileSignedTokens(folderName, targetTableId)

    if (!files || files.length === 0) {
        throw new Error('No files available for download')
    }

    await Promise.all(
        files.map((file) => downloadS3File(file.signed_token, file.file_name))
    )
}